import type { InsertCustomRoleMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import { InsertCustomRoleDocument } from '@risksmart-app/web-graphql-client/generated/graphql';

import { createMutationHook } from '@/utils/hook-factories';

export interface CreateCustomRoleInput {
  name: string;
  description?: string | null;
  roleKeys: string[];
  userIds: string[];
}

export const useCreateCustomRole = createMutationHook({
  name: 'createCustomRole',
  mutationOptions: (trpc, onSuccess) =>
    trpc.frontend.customRole.customRoleCreate.mutationOptions({ onSuccess }),
  mapInput: (input: CreateCustomRoleInput) => input,
  // Invalidate the custom-role list family — used by the settings tab and
  // the linked-items picker. The legacy Apollo path evicts `custom_role`
  // for the same purpose.
  invalidate: (trpc) => [
    trpc.frontend.role.getCustomRoles.queryKey(),
    // COI-889: role and module grants feed `getTypePermissions` and
    // `checkNavigationVisibility`, both cached for 60 min with
    // `refetchOnMount: false`.
    trpc.frontend.permission.pathKey(),
  ],
  // Maps the tRPC `customRoleCreate` response back into the Apollo shape so
  // call sites can keep using the existing `result.customRoleInsert?.Id`
  // accessor regardless of which branch ran.
  mapResponse: (result: { Id: string }): InsertCustomRoleMutation => ({
    customRoleInsert: {
      __typename: 'IdOutput',
      Id: result.Id,
    },
  }),
  graphqlDocument: InsertCustomRoleDocument,
  graphqlVariables: (input: CreateCustomRoleInput) => ({
    input: {
      Name: input.name,
      Description: input.description ?? undefined,
      RoleKeys: input.roleKeys,
      UserIds: input.userIds,
    },
  }),
  evictFields: ['custom_role'],
});
